/*
  Welcome popup: sends an email + mobile number to OneSignal (CAROL app), so that the
  Welcome Journey can send the code. Markup, copy and settings are in
  sections/carol-welcome-popup.liquid.

  - The OneSignal Web SDK is loaded only on submit, so no page pays for it up front.
    Push is never asked for: no prompts, no service worker.
  - The user is identified by the lowercased email as external_id, the same id the cart
    Flow uses, so `order_placed` stops this Journey too.
  - SMS is US only (Olga, 2026-10-05). A US number becomes an SMS subscription; any
    other number is kept as the `phone` tag and is never texted.
  - Offer "guide" (the default since 2026-10-30): the contact is tagged
    lead_source=longevity_guide and gets no signup_source, so the Longevity Guide Journey
    emails the guide and the Welcome Journey (the code) is skipped. Same tags as the guide
    page's gate (assets/carolbike-longevity-guide-form.js). Offer "code" is the old
    MOBILE100 popup.
  - Every storage access is wrapped: private windows and blocked storage throw.
  - While the section's "Show the popup to shoppers" is off, nothing opens unless the URL
    carries ?cwp_test=1, which opens it at once for a test sign-up.
*/
(function () {
  'use strict';

  var root = document.getElementById('CarolWelcomePopup');
  var cfgEl = document.getElementById('CarolWelcomeConfig');
  if (!root || !cfgEl) return;

  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var KEY_DONE = 'carol_welcome_done';
  var KEY_SNOOZE = 'carol_welcome_snoozed_at';
  var SDK = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';

  // Dial codes for the markets CAROL sells into most. Anyone else types a leading +.
  var DIAL = { US: '1', CA: '1', GB: '44', IE: '353', DE: '49', FR: '33', NL: '31', BE: '32', AT: '43', CH: '41',
    ES: '34', IT: '39', PT: '351', SE: '46', DK: '45', NO: '47', FI: '358', PL: '48', AU: '61', NZ: '64',
    SG: '65', HK: '852', JP: '81', KR: '82', AE: '971', ZA: '27', IN: '91' };

  function store(key, value) {
    try {
      if (value === undefined) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, value);
    } catch (e) { return null; }
    return null;
  }

  function amountLabel() {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: cfg.currency || 'USD',
        maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(cfg.amount);
    } catch (e) { return String(cfg.amount); }
  }

  function snoozed() {
    if (store(KEY_DONE)) return true;
    var at = parseInt(store(KEY_SNOOZE) || '0', 10);
    return at && (Date.now() - at) < cfg.snoozeDays * 864e5;
  }

  // Another modal already up (cart drawer, a Klaviyo form, a video): try again later.
  // Visible means on screen: the theme's cart drawer keeps role="dialog" aria-modal="true"
  // in the DOM while closed (visibility: hidden, translated off screen), and an
  // offsetParent test alone counted it as open on every page, so the popup never showed.
  function onScreen(el) {
    // offsetParent is null for position:fixed, which is how most pop-ups sit, so test
    // for layout boxes instead (none means display:none somewhere up the tree).
    if (!el.getClientRects().length) return false;
    var cs = window.getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.opacity === '0') return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.right > 0 && r.bottom > 0 &&
      r.left < window.innerWidth && r.top < window.innerHeight;
  }

  function busy() {
    var open = document.querySelectorAll('[role="dialog"][aria-modal="true"], dialog[open], cart-drawer.active');
    for (var i = 0; i < open.length; i++) {
      if (!root.contains(open[i]) && onScreen(open[i])) return true;
    }
    return false;
  }

  var amount = amountLabel();
  root.querySelectorAll('[data-cwp-amount]').forEach(function (el) { el.textContent = amount; });

  var card = root.querySelector('[role="dialog"]');
  var form = root.querySelector('[data-cwp-form]');
  var error = root.querySelector('[data-cwp-error]');
  var ask = root.querySelector('[data-cwp-ask]');
  var done = root.querySelector('[data-cwp-done]');
  var email = form.elements.email;
  var phone = form.elements.phone;
  var lastFocus = null;
  var shown = false;
  var submitted = false;

  phone.placeholder = DIAL[cfg.country] ? '+' + DIAL[cfg.country] + ' ' : '+';

  function open() {
    if (shown || snoozed()) return;
    if (klaviyoOpen()) { shown = true; return; }
    if (busy()) { setTimeout(open, 15000); return; }
    shown = true;
    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(function () {
      root.classList.add('is-open');
      email.focus({ preventScroll: true });
    });
    document.addEventListener('keydown', onKey);
    watch = setInterval(function () { if (!submitted && klaviyoOpen()) close(true); }, 500);
    if (window.dataLayer) window.dataLayer.push({ event: 'carol_welcome_popup_view' });
  }

  // A Klaviyo form (still live on the site during the move to OneSignal, 2026-10) can open
  // after ours. When one shows, ours steps aside for this page view, without snoozing.
  function klaviyoOpen() {
    var k = document.querySelector('[aria-label="POPUP Form"], [aria-label="FLYOUT Form"]');
    return !!(k && !root.contains(k) && onScreen(k));
  }
  var watch = null;

  function close(yieldOnly) {
    if (!shown) return;
    if (!submitted && !yieldOnly) store(KEY_SNOOZE, String(Date.now()));
    if (watch) { clearInterval(watch); watch = null; }
    root.classList.remove('is-open');
    document.removeEventListener('keydown', onKey);
    document.documentElement.style.overflow = '';
    setTimeout(function () { root.hidden = true; }, 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    // A yield is final for this page view: two pop-ups in a row is worse than none.
    shown = !!yieldOnly;
  }

  function onKey(e) {
    if (e.key === 'Escape') { close(false); return; }
    if (e.key !== 'Tab') return;
    var f = card.querySelectorAll('button, input, a[href]');
    var list = Array.prototype.filter.call(f, function (el) { return el.offsetParent !== null && !el.disabled; });
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  }

  // E.164: keep a typed +, otherwise prefix the market's dial code (dropping a trunk 0).
  function toE164(raw) {
    var s = String(raw || '').trim();
    var plus = s.charAt(0) === '+' || s.indexOf('00') === 0;
    var digits = s.replace(/\D/g, '');
    if (s.indexOf('00') === 0) digits = digits.slice(2);
    if (!plus) {
      var dial = DIAL[cfg.country];
      if (!dial) return null;
      if (dial === '1' && digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
      if (dial !== '1') digits = digits.replace(/^0+/, '');
      digits = dial + digits;
    }
    return digits.length >= 8 && digits.length <= 15 ? '+' + digits : null;
  }

  function fail(field, message) {
    error.textContent = message;
    if (field) { field.setAttribute('aria-invalid', 'true'); field.focus(); }
  }

  function loadSdk() {
    return new Promise(function (resolve, reject) {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(function (OneSignal) { resolve(OneSignal); });
      if (document.querySelector('script[src="' + SDK + '"]')) return;
      var s = document.createElement('script');
      s.src = SDK; s.defer = true;
      s.onerror = function () { reject(new Error('sdk')); };
      document.head.appendChild(s);
      setTimeout(function () { reject(new Error('timeout')); }, 15000);
    });
  }

  function send(addr, e164) {
    var region = cfg.region;
    var tags = cfg.offer === 'guide'
      ? { lead_source: 'longevity_guide', region: region, code_value: amount }
      : { signup_source: 'popup', region: region, welcome_code: cfg.code, code_value: amount };
    if (region !== 'US') tags.phone = e164;
    // The emails only say "we've also texted it" when this tag is set (see the section).
    if (region === 'US' && cfg.smsLive) tags.sms_on = '1';
    return loadSdk().then(function (OneSignal) {
      return OneSignal.init({
        appId: cfg.appId,
        autoResubscribe: false,
        notifyButton: { enable: false },
        welcomeNotification: { disable: true },
        promptOptions: { slidedown: { prompts: [] } }
      }).then(function () {
        return OneSignal.login(addr.toLowerCase());
      }).then(function () {
        OneSignal.User.addEmail(addr.toLowerCase());
        if (region === 'US') OneSignal.User.addSms(e164);
        OneSignal.User.addTags(tags);
      });
    });
  }

  phone.addEventListener('change', function () {
    var n = toE164(phone.value);
    if (n) phone.value = n;
  });

  form.addEventListener('input', function (e) {
    e.target.removeAttribute('aria-invalid');
    error.textContent = '';
  });

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var addr = email.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(addr)) return fail(email, cfg.msgEmail || 'Please enter a valid email address.');
    var e164 = toE164(phone.value);
    if (!e164) return fail(phone, cfg.msgPhone || 'Please enter your mobile number, including the country code (for example +44).');
    // Write it back so HubSpot's form capture records the same E.164 number.
    phone.value = e164;

    var button = form.querySelector('.cwp__submit');
    button.disabled = true;
    send(addr, e164).then(function () {
      submitted = true;
      store(KEY_DONE, String(Date.now()));
      ask.hidden = true;
      done.hidden = false;
      done.focus({ preventScroll: true });
      if (window.dataLayer) window.dataLayer.push({ event: 'carol_welcome_signup', signup_region: cfg.region, signup_offer: cfg.offer });
    }).catch(function () {
      button.disabled = false;
      fail(null, cfg.msgError || 'Something went wrong on our side. Please try again in a moment, or email us at info@carolbike.com.');
    });
  });

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cwp-close]')) close(false);
  });

  if (cfg.preview || /[?&]cwp_test=1\b/.test(location.search)) {
    try { window.localStorage.removeItem(KEY_DONE); window.localStorage.removeItem(KEY_SNOOZE); } catch (e) {}
    open();
    return;
  }
  if (!cfg.enabled || snoozed()) return;

  setTimeout(open, cfg.delayMs);
  // Desktop exit intent: the pointer leaves through the top of the window.
  if (window.matchMedia('(pointer: fine)').matches) {
    setTimeout(function () {
      document.addEventListener('mouseout', function onOut(e) {
        if (!e.relatedTarget && e.clientY <= 0) { document.removeEventListener('mouseout', onOut); open(); }
      });
    }, 4000);
  }
})();
